import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import { TrendingUp, MapPin, Users, Search, BellRing, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const WEEKDAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const HALF_DAY_LABELS = { matin: 'Matin', apres_midi: 'Après-midi' };
const GREEN = '#A5BBA8';
const TERRA = '#C86B51';

const labelWeekday = (k) => (k >= 0 && k <= 6 ? WEEKDAYS[k] : 'Non précisé');
const labelHalfDay = (k) => HALF_DAY_LABELS[k] || 'Indifférent';

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white rounded-xl border border-[#E3DED5] p-5 flex items-center gap-4" data-testid={testId}>
    <div className="w-12 h-12 rounded-lg bg-[#A5BBA8]/15 flex items-center justify-center">
      <Icon className="w-6 h-6 text-[#A5BBA8]" />
    </div>
    <div>
      <p className="text-2xl font-semibold text-[#2A312C]">{value}</p>
      <p className="text-sm text-[#5C6A61]">{label}</p>
    </div>
  </div>
);

const BarList = ({ title, rows, labelFn, testId, accent = GREEN }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-white rounded-xl border border-[#E3DED5] p-5" data-testid={testId}>
      <h3 className="font-semibold text-[#2A312C] mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-[#5C6A61]">Aucune donnée</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#2A312C]">{labelFn ? labelFn(r.key) : (r.label || r.key || '—')}</span>
                <span className="text-[#5C6A61] font-medium">{r.count}</span>
              </div>
              <div className="h-2 rounded-full bg-[#F9F6F0]">
                <div className="h-2 rounded-full" style={{ width: `${(r.count / max) * 100}%`, backgroundColor: accent }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function UnmetDemandDashboard() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        adminApi.getUnmetDemandStats({}),
        adminApi.getUnmetDemandList({ limit: 50 }),
      ]);
      setStats(s.data);
      setItems(l.data.items || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des demandes non satisfaites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await adminApi.resolveUnmetDemand(id);
      toast.success('Demande marquée comme traitée');
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#5C6A61] py-10" data-testid="unmet-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Chargement...
      </div>
    );
  }

  const monthly = (stats?.monthly || []).map((r) => ({ name: r.key, count: r.count }));
  const byWeekday = [...(stats?.by_weekday || [])].sort((a, b) => a.key - b.key);
  const byReason = stats?.by_reason || [];

  return (
    <div className="space-y-8" data-testid="unmet-demand-dashboard">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#2A312C]">
          Demande non satisfaite
        </h2>
        <p className="text-[#5C6A61] mt-1">
          Mesure automatique de la demande que Mitonner ne peut pas encore satisfaire — outil d'aide à la décision (recrutement, extension géographique, ouverture de nouveaux jours).
        </p>
      </div>

      {(!stats || stats.total === 0) ? (
        <div className="bg-white rounded-xl border border-[#E3DED5] p-8 text-center text-[#5C6A61]" data-testid="unmet-empty">
          <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-[#A5BBA8]" />
          Aucune demande non satisfaite enregistrée pour le moment. Les données apparaîtront automatiquement dès qu'un client recherchera un créneau indisponible.
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard icon={AlertTriangle} label="Demandes non satisfaites" value={stats.total} testId="stat-total" />
            <StatCard icon={Search} label="Recherches infructueuses" value={stats.total_searches} testId="stat-searches" />
            <StatCard icon={BellRing} label="Alertes client demandées" value={stats.alert_optins} testId="stat-optins" />
          </div>

          {monthly.length > 0 && (
            <div className="bg-white rounded-xl border border-[#E3DED5] p-5" data-testid="unmet-monthly-chart">
              <h3 className="font-semibold text-[#2A312C] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#A5BBA8]" /> Évolution mensuelle
              </h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E3DED5" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#5C6A61' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#5C6A61' }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} fill={GREEN} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white rounded-xl border border-[#E3DED5] p-5" data-testid="unmet-reason-chart">
            <h3 className="font-semibold text-[#2A312C] mb-4">Causes des refus</h3>
            <ResponsiveContainer width="100%" height={Math.max(160, byReason.length * 44)}>
              <BarChart data={byReason.map((r) => ({ name: r.label || r.key, count: r.count }))} layout="vertical" margin={{ left: 40 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#5C6A61' }} />
                <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 12, fill: '#2A312C' }} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {byReason.map((_, i) => <Cell key={i} fill={i === 0 ? TERRA : GREEN} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <BarList title="Secteurs les plus demandés" rows={stats.by_sector} testId="unmet-by-sector" accent={TERRA} />
            <BarList title="Communes les plus demandeuses" rows={stats.by_city} testId="unmet-by-city" />
            <BarList title="Jours les plus demandés" rows={byWeekday} labelFn={labelWeekday} testId="unmet-by-weekday" />
            <BarList title="Créneaux souhaités" rows={stats.by_half_day} labelFn={labelHalfDay} testId="unmet-by-halfday" />
            <BarList title="Formules recherchées" rows={stats.by_formula} testId="unmet-by-formula" />
            <BarList title="Options recherchées" rows={stats.by_option} testId="unmet-by-option" />
          </div>

          <div className="bg-white rounded-xl border border-[#E3DED5] p-5" data-testid="unmet-table">
            <h3 className="font-semibold text-[#2A312C] mb-4">Demandes récentes</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#5C6A61] border-b border-[#E3DED5]">
                    <th className="py-2 pr-3">Date demandée</th>
                    <th className="py-2 pr-3">Secteur / Commune</th>
                    <th className="py-2 pr-3">Formule</th>
                    <th className="py-2 pr-3">Cause</th>
                    <th className="py-2 pr-3">Recherches</th>
                    <th className="py-2 pr-3">Alerte</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.demand_id} className={`border-b border-[#F0EBE0] ${it.resolved ? 'opacity-50' : ''}`} data-testid={`unmet-row-${it.demand_id}`}>
                      <td className="py-2 pr-3 text-[#2A312C]">{it.requested_date} <span className="text-[#5C6A61]">({labelWeekday(it.requested_weekday)})</span></td>
                      <td className="py-2 pr-3">{it.sector_name || 'Hors secteur'}{it.city ? ` · ${it.city}` : ''}</td>
                      <td className="py-2 pr-3">{it.formula}{it.options?.length ? ` +${it.options.length} opt.` : ''}</td>
                      <td className="py-2 pr-3">{it.reason_label}</td>
                      <td className="py-2 pr-3">{it.search_count}</td>
                      <td className="py-2 pr-3">{it.alert_optin ? <BellRing className="w-4 h-4 text-[#C86B51]" /> : '—'}</td>
                      <td className="py-2 pr-3">
                        {!it.resolved && (
                          <button onClick={() => resolve(it.demand_id)} className="text-[#A5BBA8] hover:text-[#94aa97] flex items-center gap-1" data-testid={`resolve-${it.demand_id}`}>
                            <CheckCircle2 className="w-4 h-4" /> Traité
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
